import type { components } from '@tallerya/api-client';
import { dinero, fecha, numero, useTraduccion } from '@tallerya/i18n';
import { AvisoError, Ayuda, Boton, Chip, IconoMas, Menu, type Columna, type ElementoMenu } from '@tallerya/ui';
import { useState, type MouseEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { useAccionDeFila } from '../../componentes/useAccionDeFila';
import { usePermisos } from '../../permisos/usePermiso';
import { useTipos } from '../catalogos/datos';
import { EliminarCliente } from './EliminarCliente';
import { PanelHistorial } from './PanelHistorial';
import {
  useActivosCliente,
  useCambiosCliente,
  useFichaCliente,
  useOrdenesCliente,
  version,
  type ActivoCliente,
  type FichaCliente as Ficha,
  type OrdenCliente,
} from './datos';

// La ficha del cliente (#105): «Ficha del cliente» y «Sin vehículos» del Figma.
// Es de solo lectura; se edita con «Editar cliente», que abre el formulario de
// #104 y vuelve acá con «Atrás».
//
// Lo propio de esta pantalla:
//
//   - **la sección de activos no se llama «Vehículos» fijo.** Se llama según los
//     tipos con que trabaja el taller, ver `claseDeActivos`;
//   - la columna del identificador toma el nombre del tipo («Placa», «Serie»)
//     cuando todos los activos del cliente son del mismo tipo;
//   - las órdenes solo se piden con `order.view`, y el total puede venir vacío:
//     **lo calla la API** a quien no tiene `order.amount.view`;
//   - «Ver todas las órdenes» no está: el listado de órdenes todavía no filtra por cliente.
//   - el historial se abre en un panel a la derecha (#181, #193): son los cambios
//     del registro, y las órdenes del cliente son otra sección, «Órdenes»;
//   - las migas van arriba del contenido y no en el app bar, que en escritorio
//     no muestra el título de la pantalla.

type Tipo = components['schemas']['AssetTypeListItem'];

/** Cómo se llama la sección de activos. Cada una tiene sus textos en el catálogo. */
type Clase = 'vehiculos' | 'equipos' | 'activos';

/** Los `code` fijos de los tipos que siembra el alta del taller (`DefaultAssetTypes`). */
const VEHICULO = 'vehicle';
const EQUIPO = 'equipment';

/**
 * El nombre de la sección, de dos fuentes juntas: los tipos activos del taller y
 * los tipos de los activos que ya tiene el cliente —por si uno de esos se apagó
 * después—. Si en ese conjunto solo está el vehículo, «Vehículos»; si solo el
 * equipo, «Equipos»; con cualquier otra mezcla, «Activos».
 *
 * Se decide por el `code`, que es fijo, y no por el nombre, que el taller puede
 * cambiar.
 */
function claseDeActivos(tipos: readonly Tipo[], activos: readonly ActivoCliente[]): Clase {
  const ids = new Set([
    ...tipos.filter((tp) => tp.isActive).map((tp) => String(tp.id)),
    ...activos.map((a) => String(a.assetTypeId)),
  ]);
  const codigos = new Set([...ids].map((id) => tipos.find((tp) => String(tp.id) === id)?.code));

  if (codigos.size !== 1) return 'activos';
  if (codigos.has(VEHICULO)) return 'vehiculos';
  if (codigos.has(EQUIPO)) return 'equipos';
  return 'activos';
}

/** Activos primero, después por código; los que no tienen código, al final. */
function ordenarActivos(activos: readonly ActivoCliente[]): ActivoCliente[] {
  return [...activos].sort(
    (a, b) =>
      Number(b.isActive) - Number(a.isActive) ||
      (a.code ?? '￿').localeCompare(b.code ?? '￿', undefined, { numeric: true }),
  );
}

export function FichaCliente() {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const { id } = useParams();
  const consulta = useFichaCliente(id);
  useTituloPantalla(consulta.data?.name ?? t('admin.clientes.titulo'));

  if (consulta.isPending) return <p className="entrada">{t('app.cargando')}</p>;

  if (consulta.isError || consulta.data === null) {
    const noExiste = consulta.data === null;
    return (
      <section className="aviso">
        <h2>{noExiste ? t('admin.clientes.noExiste.titulo') : t('ui.listado.error.titulo')}</h2>
        <p>{noExiste ? t('admin.clientes.noExiste.texto') : t('ui.listado.error.texto')}</p>
        <Boton variante="secundario" onClick={() => void navegar('/clientes')}>
          {t('admin.clientes.volver')}
        </Boton>
      </section>
    );
  }

  return <Contenido ficha={consulta.data} />;
}

function Contenido({ ficha }: { ficha: Ficha }) {
  const { t, idioma } = useTraduccion();
  const navegar = useNavigate();
  const permisos = usePermisos();
  const id = String(ficha.id);
  // Si el listado está detrás de esta ficha. La edición lo necesita para volver
  // a él, con sus filtros, cuando el cliente se elimina.
  const desdeListado = (useLocation().state as { desdeListado?: boolean } | null)?.desdeListado ?? false;

  const tipos = useTipos();
  const activos = useActivosCliente(id);
  // Mientras no se sepa con qué tipos trabaja el taller, la sección no tiene
  // nombre todavía: se espera en vez de decir «Activos» y cambiar a «Vehículos».
  const clase = tipos.data && activos.data ? claseDeActivos(tipos.data, activos.data) : null;

  const documento = ficha.docIdentifier
    ? [ficha.docTypeCode, ficha.docIdentifier].filter(Boolean).join(' ')
    : null;

  // El «•••» junto a «Editar cliente» (#160): lo del menú de la fila del
  // listado que no es abrir la ficha ni editar. Eliminar pide confirmación con
  // la misma ventana; activar y desactivar van directo, como en el listado.
  const cambios = useCambiosCliente();
  const { hacer, problema } = useAccionDeFila();
  const [eliminando, setEliminando] = useState(false);
  // El historial empieza cerrado y no se pide hasta abrirlo (#193).
  const [viendoHistorial, setViendoHistorial] = useState(false);
  const nombre = { nombre: ficha.name };
  const masAcciones: ElementoMenu[] = [
    ficha.isActive
      ? {
          etiqueta: t('admin.clientes.menu.desactivar'),
          onSeleccionar: () =>
            void hacer(
              cambios.cambiarEstado(id, version(ficha.version), false),
              t('admin.clientes.aviso.desactivado', nombre),
            ),
        }
      : {
          etiqueta: t('admin.clientes.menu.activar'),
          onSeleccionar: () =>
            void hacer(
              cambios.cambiarEstado(id, version(ficha.version), true),
              t('admin.clientes.aviso.activado', nombre),
            ),
        },
    { tipo: 'divisor' },
    { etiqueta: t('admin.clientes.menu.eliminar'), peligro: true, onSeleccionar: () => setEliminando(true) },
  ];
  // Eliminado, la ficha ya no existe: se vuelve al listado que hay detrás, con
  // sus filtros, o se reemplaza esta pantalla por uno limpio.
  const alEliminar = () => void (desdeListado ? navegar(-1) : navegar('/clientes', { replace: true }));

  return (
    <>
      <nav aria-label={t('admin.clientes.ficha.migas')}>
        <ol className="ficha__migas">
          <li>
            <Link to="/clientes">{t('admin.clientes.titulo')}</Link>
          </li>
          <li aria-current="page">{ficha.name}</li>
        </ol>
      </nav>

      {problema && <AvisoError aviso={{ titulo: t('admin.clientes.error.titulo'), texto: problema }} />}

      <header className="tipos__cabecera">
        <div>
          <div className="tipos__titulo">
            <h1>{ficha.name}</h1>
            <Ayuda tema={ficha.name} texto={t('admin.clientes.ficha.info')} tono="titulo" />
            <Chip>{ficha.kind === 'company' ? t('admin.clientes.empresa') : t('admin.clientes.persona')}</Chip>
            {ficha.isActive ? (
              <Chip tono="exito">{t('admin.clientes.activo')}</Chip>
            ) : (
              <Chip>{t('admin.clientes.inactivo')}</Chip>
            )}
          </div>
          {/* El nombre comercial de una empresa (#126), entre la razón social y el resumen. */}
          {ficha.tradeName && <p className="clientes__comercial-ficha">{ficha.tradeName}</p>}
          <p className="tipos__resumen">
            {[
              ficha.code,
              documento,
              t('admin.clientes.ficha.desde', { fecha: fecha(ficha.createdAt, idioma) }),
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <div className="tipos__acciones">
          <Boton variante="secundario" onClick={() => setViendoHistorial(true)}>
            {t('admin.historial.titulo')}
          </Boton>
          <Boton
            variante="secundario"
            // `desdeFicha`: el formulario vuelve con «Atrás», que es esta ficha.
            onClick={() => void navegar(`/clientes/${id}/editar`, { state: { desdeFicha: true, desdeListado } })}
          >
            {t('admin.clientes.ficha.editar')}
          </Boton>
          <Menu
            elementos={masAcciones}
            disparador={({ alternar, atributos }) => (
              <Boton variante="secundario" soloIcono aria-label={t('admin.clientes.mas')} onClick={alternar} {...atributos}>
                •••
              </Boton>
            )}
          />
        </div>
      </header>

      <div className="ficha__secciones">
        <SeccionContacto ficha={ficha} />

        <SeccionActivos
          clienteId={id}
          inactivo={!ficha.isActive}
          clase={clase}
          activos={activos.data}
          error={tipos.isError || activos.isError}
          onReintentar={() => {
            if (tipos.isError) void tipos.refetch();
            if (activos.isError) void activos.refetch();
          }}
        />

        {/* Sin `order.view`, ni se pide: la API respondería 403. Mientras los
            permisos cargan tampoco se dibuja, para no mostrarla y quitarla. */}
        {!permisos.cargando && permisos.puede('order.view') && (
          <SeccionOrdenes clienteId={id} clase={clase} />
        )}

        {ficha.notes && (
          <Seccion titulo={t('admin.clientes.ficha.notas')}>
            <p className="ficha__notas">{ficha.notes}</p>
          </Seccion>
        )}

        <PanelHistorial clienteId={id} abierto={viendoHistorial} onCerrar={() => setViendoHistorial(false)} />
      </div>

      <EliminarCliente
        cliente={eliminando ? ficha : null}
        onCerrar={() => setEliminando(false)}
        onEliminado={alEliminar}
      />
    </>
  );
}

function Seccion({
  titulo,
  info,
  accion,
  children,
}: {
  titulo: string;
  /** Qué es la sección, tras el ⓘ del título (#121). */
  info?: string;
  accion?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="tipos__seccion">
      <div className="tipos__seccion-cabecera">
        <div className="titulo-con-ayuda titulo-con-ayuda--seccion">
          <h2>{titulo}</h2>
          {info && <Ayuda tema={titulo} texto={info} tono="titulo" />}
        </div>
        {accion}
      </div>
      {children}
    </section>
  );
}

// ------------------------------------------------------------ Contacto

function SeccionContacto({ ficha }: { ficha: Ficha }) {
  const { t } = useTraduccion();

  // «2260-4578 · Oficina»: el valor y, si tiene, su etiqueta.
  const conEtiqueta = (c: Ficha['contacts'][number]) => [c.value, c.label].filter(Boolean).join(' · ');

  const telefonos = ficha.contacts.filter((c) => c.kind === 'phone');
  const correos = ficha.contacts.filter((c) => c.kind === 'email');

  // Solo las filas que tienen algo: un «Otros correos» vacío es un hueco.
  const filas: [string, string[]][] = [
    [t('admin.clientes.ficha.contacto.telefonoPrincipal'), telefonos.filter((c) => c.isPrimary).map(conEtiqueta)],
    [t('admin.clientes.ficha.contacto.otrosTelefonos'), telefonos.filter((c) => !c.isPrimary).map(conEtiqueta)],
    [t('admin.clientes.ficha.contacto.correoPrincipal'), correos.filter((c) => c.isPrimary).map(conEtiqueta)],
    [t('admin.clientes.ficha.contacto.otrosCorreos'), correos.filter((c) => !c.isPrimary).map(conEtiqueta)],
    [t('admin.clientes.ficha.contacto.direccion'), ficha.address ? [ficha.address] : []],
  ];
  const conDatos = filas.filter(([, valores]) => valores.length > 0);

  return (
    <Seccion titulo={t('admin.clientes.ficha.contacto.titulo')} info={t('admin.clientes.ficha.contacto.texto')}>
      {conDatos.length === 0 ? (
        <p className="ficha__nota">{t('admin.clientes.ficha.contacto.vacio')}</p>
      ) : (
        <dl className="ficha__datos">
          {conDatos.map(([etiqueta, valores]) => (
            <div key={etiqueta} className="ficha__dato">
              <dt>{etiqueta}</dt>
              {valores.map((v) => (
                <dd key={v}>{v}</dd>
              ))}
            </div>
          ))}
        </dl>
      )}
    </Seccion>
  );
}

// ------------------------------------------------------------ Activos

function SeccionActivos({
  clienteId,
  inactivo,
  clase,
  activos,
  error,
  onReintentar,
}: {
  clienteId: string;
  /** A un cliente inactivo no se le registran activos (#177): no se ofrece. */
  inactivo: boolean;
  clase: Clase | null;
  activos: ActivoCliente[] | undefined;
  error: boolean;
  onReintentar: () => void;
}) {
  const { t } = useTraduccion();
  const navegar = useNavigate();

  if (error) return <ErrorDeSeccion onReintentar={onReintentar} />;
  if (!clase || !activos) return <p className="entrada">{t('app.cargando')}</p>;

  const agregar = !inactivo && (
    <Boton
      icono={<IconoMas />}
      onClick={() => void navegar(`/activos/nuevo?cliente=${clienteId}`, { state: { desdeFicha: true } })}
    >
      {t(`admin.clientes.ficha.activos.agregar.${clase}`)}
    </Boton>
  );

  // «Placa» si todos los activos son de un mismo tipo; si hay de varios, cada
  // fila dice el suyo y el encabezado queda genérico.
  const unSoloTipo = new Set(activos.map((a) => String(a.assetTypeId))).size === 1;
  // `desdeFicha`: la ficha del activo (#187) vuelve acá, y no al listado de Activos.
  const abrir = (a: ActivoCliente) => void navegar(`/activos/${String(a.id)}`, { state: { desdeFicha: true } });

  const columnas: Columna<ActivoCliente>[] = [
    {
      id: 'codigo',
      titulo: t('admin.clientes.ficha.activos.columna.codigo'),
      ancho: 110,
      // Un botón, para llegar con el teclado: la fila entera solo responde al dedo y al mouse.
      celda: (a) => (
        <button type="button" className="tipos__enlace" onClick={() => abrir(a)}>
          {a.code ?? '—'}
        </button>
      ),
    },
    {
      id: 'marca',
      titulo: t('admin.clientes.ficha.activos.columna.marcaModelo'),
      celda: (a) => [a.brandName, a.modelName].filter(Boolean).join(' ') || '—',
    },
    {
      id: 'tipo',
      titulo: t('admin.clientes.ficha.activos.columna.tipo'),
      ancho: 120,
      celda: (a) => <Chip>{a.assetTypeName}</Chip>,
    },
    {
      id: 'identificador',
      titulo: unSoloTipo && activos[0] ? activos[0].identifierLabel : t('admin.clientes.ficha.activos.columna.identificador'),
      ancho: 150,
      celda: (a) =>
        !a.identifier ? (
          '—'
        ) : unSoloTipo ? (
          a.identifier
        ) : (
          <span className="activos__identificador">
            <small>{a.identifierLabel}</small>
            {a.identifier}
          </span>
        ),
    },
    {
      id: 'anio',
      titulo: t('admin.clientes.ficha.activos.columna.anio'),
      ancho: 70,
      alinear: 'centro',
      celda: (a) => a.year ?? '—',
    },
    {
      id: 'ordenes',
      titulo: t('admin.clientes.ficha.activos.columna.ordenes'),
      ancho: 90,
      alinear: 'centro',
      celda: (a) => a.orderCount,
    },
    {
      id: 'estado',
      titulo: t('admin.clientes.ficha.activos.columna.estado'),
      ancho: 110,
      celda: (a) =>
        a.isActive ? <Chip tono="exito">{t('admin.activos.activo')}</Chip> : <Chip>{t('admin.activos.inactivo')}</Chip>,
    },
  ];

  return (
    <Seccion
      titulo={t(`admin.clientes.ficha.activos.${clase}`, { n: activos.length })}
      info={t('admin.clientes.ficha.activos.texto')}
      {...(activos.length > 0 && { accion: agregar })}
    >
      {activos.length === 0 ? (
        <Vacio
          titulo={t(`admin.clientes.ficha.activos.vacio.${clase}`)}
          texto={t('admin.clientes.ficha.activos.vacioTexto')}
          accion={agregar}
        />
      ) : (
        <Tabla columnas={columnas} filas={ordenarActivos(activos)} claveFila={(a) => String(a.id)} onFila={abrir} />
      )}
    </Seccion>
  );
}

// ------------------------------------------------------------ Órdenes

function SeccionOrdenes({ clienteId, clase }: { clienteId: string; clase: Clase | null }) {
  const { t, idioma } = useTraduccion();
  const consulta = useOrdenesCliente(clienteId, true);

  const titulo = t('admin.clientes.ficha.ordenes.titulo');
  const texto = t('admin.clientes.ficha.ordenes.texto');

  if (consulta.isError) {
    return (
      <Seccion titulo={titulo} info={texto}>
        <ErrorDeSeccion onReintentar={() => void consulta.refetch()} />
      </Seccion>
    );
  }

  if (consulta.isPending) {
    return (
      <Seccion titulo={titulo} info={texto}>
        <p className="entrada">{t('app.cargando')}</p>
      </Seccion>
    );
  }

  const { currencyCode: moneda, items: ordenes } = consulta.data;

  // Sin la moneda del taller no se inventa una: el número va solo.
  const total = (o: OrdenCliente) => {
    if (o.totalAmount === null) return '—';
    const valor = Number(o.totalAmount);
    return moneda ? dinero(valor, moneda, idioma) : numero(valor, idioma);
  };

  const columnas: Columna<OrdenCliente>[] = [
    {
      id: 'folio',
      titulo: t('admin.clientes.ficha.ordenes.columna.folio'),
      ancho: 110,
      celda: (o) => o.orderNumber ?? t('admin.clientes.ficha.ordenes.sinFolio'),
    },
    {
      id: 'activo',
      // Sin la clase —los tipos o los activos no cargaron— el nombre genérico:
      // las órdenes no esperan a una lectura que no es suya.
      titulo: t(`admin.clientes.ficha.ordenes.columna.${clase ?? 'activos'}`),
      // «Toyota Hilux 2.4 · P 123-456».
      celda: (o) =>
        [[o.brandName, o.modelName].filter(Boolean).join(' '), o.identifier].filter(Boolean).join(' · ') || '—',
    },
    {
      id: 'sucursal',
      titulo: t('admin.clientes.ficha.ordenes.columna.sucursal'),
      ancho: 130,
      celda: (o) => o.locationName ?? '—',
    },
    {
      id: 'estado',
      titulo: t('admin.clientes.ficha.ordenes.columna.estado'),
      ancho: 150,
      celda: (o) => (
        <Chip>
          {o.status.color && (
            <span className="ficha__punto" style={{ background: o.status.color }} aria-hidden="true" />
          )}
          {o.status.name}
        </Chip>
      ),
    },
    {
      id: 'abierta',
      titulo: t('admin.clientes.ficha.ordenes.columna.abierta'),
      ancho: 120,
      celda: (o) => fecha(o.openedAt, idioma),
    },
    {
      id: 'total',
      titulo: t('admin.clientes.ficha.ordenes.columna.total'),
      ancho: 110,
      alinear: 'fin',
      celda: total,
    },
  ];

  return (
    <Seccion titulo={titulo} info={texto}>
      {ordenes.length === 0 ? (
        <Vacio
          titulo={t('admin.clientes.ficha.ordenes.vacio')}
          texto={t('admin.clientes.ficha.ordenes.vacioTexto')}
        />
      ) : (
        // Las filas no se tocan todavía: la pantalla de la orden llega en la fase 3.
        <Tabla columnas={columnas} filas={ordenes} claveFila={(o) => String(o.id)} />
      )}
    </Seccion>
  );
}

// ------------------------------------------------------------ Piezas

/**
 * Una tabla de la ficha, con las clases de `Listado` para que se vea igual que
 * los listados. `Listado` no sirve acá: es una página entera, con su título,
 * su barra de filtros y su paginación.
 */
function Tabla<T>({
  columnas,
  filas,
  claveFila,
  onFila,
}: {
  columnas: readonly Columna<T>[];
  filas: readonly T[];
  claveFila: (fila: T) => string;
  onFila?: (fila: T) => void;
}) {
  const alinear = (c: Columna<T>) =>
    c.alinear === 'centro' ? 'ui-tabla__centro' : c.alinear === 'fin' ? 'ui-tabla__fin' : undefined;

  return (
    <div className="ui-listado__marco">
      <table className="ui-tabla">
        <colgroup>
          {columnas.map((c) => (
            <col key={c.id} style={c.ancho ? { width: c.ancho } : undefined} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columnas.map((c) => (
              <th key={c.id} scope="col" className={alinear(c)}>
                {c.titulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((fila) => (
            <tr
              key={claveFila(fila)}
              {...(onFila && {
                className: 'ui-tabla__fila--tocable',
                // El botón de la celda ya abre lo mismo: no se abre dos veces.
                onClick: (e: MouseEvent<HTMLTableRowElement>) => {
                  if ((e.target as Element).closest('button, a')) return;
                  onFila(fila);
                },
              })}
            >
              {columnas.map((c) => (
                <td key={c.id} className={alinear(c)}>
                  {c.celda(fila)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Vacio({ titulo, texto, accion }: { titulo: string; texto: string; accion?: ReactNode }) {
  return (
    <div className="ficha__vacio">
      <p className="ficha__vacio-titulo">{titulo}</p>
      <p>{texto}</p>
      {accion}
    </div>
  );
}

function ErrorDeSeccion({ onReintentar }: { onReintentar: () => void }) {
  const { t } = useTraduccion();
  return (
    <div className="ficha__vacio" role="alert">
      <p className="ficha__vacio-titulo">{t('ui.listado.error.titulo')}</p>
      <p>{t('ui.listado.error.texto')}</p>
      <Boton variante="secundario" onClick={onReintentar}>
        {t('app.reintentar')}
      </Boton>
    </div>
  );
}
